import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, http } from '@/api/client'

export type ImportStatus = 'queued' | 'processing' | 'completed' | 'failed'

export interface ImportBatch {
  id: number
  type: string
  fileName: string
  status: ImportStatus
  totalRows: number
  importedRows: number
  failedRows: number
  progress: number
  errors: { row: number; message: string }[]
  failureReason: string | null
  createdBy: string
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
}

const isRunning = (b: ImportBatch | undefined) =>
  b !== undefined && (b.status === 'queued' || b.status === 'processing')

export const importsQuery = () =>
  queryOptions({
    queryKey: ['imports'],
    queryFn: () => api.get<ImportBatch[]>('/imports'),
    // Keep polling while any batch is still being processed by the worker.
    refetchInterval: (q) => (q.state.data?.some((b) => isRunning(b)) ? 2000 : false),
  })

export const importQuery = (id: number) =>
  queryOptions({
    queryKey: ['imports', id],
    queryFn: () => api.get<ImportBatch>(`/imports/${id}`),
    refetchInterval: (q) => (isRunning(q.state.data) ? 1500 : false),
  })

export function useStartStudentImport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fileId: number) => api.post<ImportBatch>('/imports/students', { fileId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['imports'] }),
  })
}

export async function downloadTemplate(): Promise<void> {
  const res = await http.get<Blob>('/imports/students/template', { responseType: 'blob' })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = 'students-import-template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export const importStatusTone: Record<ImportStatus, 'info' | 'warning' | 'success' | 'danger'> = {
  queued: 'info',
  processing: 'warning',
  completed: 'success',
  failed: 'danger',
}
